
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

/**
 * Simulates scanning a backup file for viruses/malware
 * In a real implementation, this would integrate with an actual virus scanning API
 */
export const scanBackupForThreats = async (
  backupId: string, 
  websiteId: string
): Promise<{
  isClean: boolean;
  threatDetails?: string;
  scanDate: string;
}> => {
  console.log(`Scanning backup ${backupId} for website ${websiteId} for threats...`);
  
  try {
    // This would be a real API call to a virus scanning service in production
    // For demo purposes, we'll simulate a scan with random results
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call delay
    
    // Get current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("Authentication required to scan backups");
    }
    
    // Verify that the website belongs to the current user
    const { data: website, error: websiteError } = await supabase
      .from('websites')
      .select('id')
      .eq('id', websiteId)
      .eq('user_id', user.id)
      .single();
    
    if (websiteError || !website) {
      throw new Error("You don't have permission to scan backups for this website");
    }
    
    // Simulate scan result - mostly clean with occasional threat
    const randomValue = Math.random() * 100;
    const isClean = randomValue > 15; // 85% chance of clean
    
    const threatTypes = [
      "Suspicious PHP code detected",
      "Potential SQL injection backdoor",
      "Known malware signature found",
      "Encrypted suspicious payload",
      "WordPress core file modification"
    ];
    
    const scanResult = {
      isClean,
      threatDetails: isClean ? undefined : threatTypes[Math.floor(Math.random() * threatTypes.length)],
      scanDate: new Date().toISOString()
    };
    
    // Log the scan result
    console.log(`Scan result for backup ${backupId}:`, scanResult);
    
    // Store scan result in Supabase
    const { error: insertError } = await supabase
      .from('backup_scans')
      .upsert({
        backup_id: backupId,
        website_id: websiteId,
        is_clean: isClean,
        threat_details: scanResult.threatDetails,
        scan_date: scanResult.scanDate
      });
    
    if (insertError) {
      console.error("Error storing scan result:", insertError);
    }
    
    // If a threat was found, trigger an email notification
    if (!isClean) {
      await sendThreatNotificationEmail(backupId, websiteId, scanResult.threatDetails || "Unknown threat");
    }
    
    return scanResult;
  } catch (error) {
    console.error(`Error scanning backup ${backupId}:`, error);
    
    toast({
      title: "Scan failed",
      description: "There was an error scanning your backup for threats.",
      variant: "destructive",
    });
    
    // Return a default response
    return {
      isClean: false,
      threatDetails: "Scan failed - unable to determine security status",
      scanDate: new Date().toISOString()
    };
  }
};

/**
 * Sends an email notification about a threat detected in a backup
 */
export const sendThreatNotificationEmail = async (
  backupId: string,
  websiteId: string,
  threatDetails: string
): Promise<boolean> => {
  try {
    // Get current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("Authentication required to send notifications");
    }
    
    // Call the notification edge function
    const { data, error } = await supabase.functions.invoke('send-threat-notification', {
      body: {
        backupId,
        websiteId,
        threatDetails,
        userId: user.id // Pass the user ID to the edge function
      }
    });
    
    if (error) {
      throw error;
    }
    
    console.log("Threat notification email sent successfully:", data);
    return true;
  } catch (error) {
    console.error("Error sending threat notification email:", error);
    return false;
  }
};

/**
 * Gets the security status of a backup
 */
export const getBackupSecurityStatus = async (backupId: string): Promise<{
  isScanned: boolean;
  isClean: boolean;
  threatDetails?: string;
  scanDate?: string;
}> => {
  try {
    // Get current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("Authentication required to check backup security status");
      return { isScanned: false, isClean: false };
    }
    
    // Fetch scan result from Supabase with website ownership verification
    const { data: scanData, error } = await supabase
      .from('backup_scans')
      .select('*, websites!inner(*)')
      .eq('backup_id', backupId)
      .eq('websites.user_id', user.id)
      .single();
    
    if (error) {
      console.error("Error fetching security status:", error);
      return { isScanned: false, isClean: false };
    }
    
    if (!scanData) {
      return { isScanned: false, isClean: false };
    }
    
    return {
      isScanned: true,
      isClean: scanData.is_clean,
      threatDetails: scanData.threat_details,
      scanDate: scanData.scan_date
    };
  } catch (error) {
    console.error("Error getting backup security status:", error);
    return { isScanned: false, isClean: false };
  }
};
